/**
 * LeadsDashboardParts.jsx - the sections of the Leads Dashboard
 * (views/LeadsDashboardView, Paul's D-01..D-06): the card frame and its
 * "Live" tag, the leads behind the KPI cards, the stage movement table, the
 * Cold summary, source performance and the two attention lists.
 *
 * Each part draws numbers that components/crm/leadsDashboard.js already
 * computed; nothing here fetches or counts. Every lead row is a button that
 * sets window.location.hash = leadHash(id), which opens the record on the
 * Leads page (the same link the Dashboard's No next step list uses).
 */
import { Explainer } from '../ui/Explainer';
import { STAGE_COLORS, LEAD_STATUS_COLORS } from '../../data/crmStages';
import { fmtDate } from '../../utils/format';
import { leadHash } from './nextStep';
import { NoNextStepGroup } from './NoNextStepCard';
import { STALL_DAYS } from './leadsDashboard';

const FONT = "'Montserrat', sans-serif";
export const DASH_CARD = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #ECECEC',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  fontFamily: FONT,
  minWidth: 0,
};
const HEAD = { fontSize: 9.5, fontWeight: 800, color: '#5A5A5A', letterSpacing: 1.2, textTransform: 'uppercase' };
const MUTED = { fontSize: 10, color: '#8A8A8A' };
const TH = {
  ...HEAD,
  fontSize: 8.5,
  padding: '8px 12px',
  textAlign: 'left',
  borderBottom: '1px solid #EEE',
  background: '#FAFAF7',
  whiteSpace: 'nowrap',
};
const THN = { ...TH, textAlign: 'right' };
const TD = { padding: '8px 12px', borderBottom: '1px solid #F5F5F2', fontSize: 11, color: '#0D0D0D' };
const TDN = { ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
// the attention lists scroll inside this height, like the Dashboard's No next step card
const LIST_MAX = 320;
const tagSt = { fontSize: 9, color: '#888', whiteSpace: 'nowrap', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis' };
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Marks a number that is the board right now: it follows the filters but not the period. */
export function LivePill() {
  return (
    <span
      title="Right now. Follows the filters but not the period."
      style={{
        fontSize: 8.5,
        fontWeight: 800,
        color: '#1B5E20',
        background: '#E8F5E9',
        border: '1px solid #C8E6C9',
        borderRadius: 8,
        padding: '1px 6px',
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
      }}
    >
      Live
    </span>
  );
}

/** A dashboard section: title, optional Live tag and "how is this calculated", then the body. */
export function DashCard({ title, live, explain, aside, children, style }) {
  return (
    <section aria-label={title} style={{ ...DASH_CARD, ...style }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderBottom: '1px solid #F0F0EC', flexWrap: 'wrap' }}
      >
        <span style={HEAD}>{title}</span>
        {live && <LivePill />}
        {explain && (
          <Explainer title={explain.title || title} formula={explain.formula} align="left">
            {explain.body}
          </Explainer>
        )}
        {aside && <span style={{ ...MUTED, marginLeft: 'auto' }}>{aside}</span>}
      </div>
      {children}
    </section>
  );
}

const StageDot = ({ stage }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: STAGE_COLORS[stage] || '#9E9E9E',
      marginRight: 8,
      flexShrink: 0,
    }}
  />
);

const StageTag = ({ stage }) => (
  <span style={{ ...tagSt, maxWidth: 150, color: STAGE_COLORS[stage] || '#666', fontWeight: 700 }}>{stage}</span>
);

/**
 * The units behind the four KPI cards, so a count can be checked by hand
 * (Paul's D-02 pass: "Cards match a manual count"). Closed until asked for.
 */
export function CountedLists({ lists, ownerName }) {
  const groups = [
    ['New leads', lists.newLeads, 'No lead was created in this period.'],
    ['Discovery meetings held', lists.meetings, 'No lead entered Discovery Meeting Complete in this period.'],
    ['NDAs executed', lists.ndas, 'No lead entered NDA Executed in this period.'],
    ['First opportunity', lists.firstOpps, 'No lead got its first opportunity in this period.'],
  ];
  return (
    <details style={{ ...DASH_CARD, marginBottom: 16 }}>
      <summary style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 11, fontWeight: 700, color: '#1B5E20' }}>
        Show the leads behind these numbers
      </summary>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 240px), 1fr))', borderTop: '1px solid #F0F0EC' }}
      >
        {groups.map(([title, rows, empty]) => (
          <div key={title} style={{ minWidth: 0, borderRight: '1px solid #F5F5F2' }}>
            <NoNextStepGroup
              title={title}
              rows={rows}
              hashOf={leadHash}
              empty={empty}
              detailOf={(r) => (r.owner ? <span style={tagSt}>{ownerName(r.owner)}</span> : null)}
            />
          </div>
        ))}
      </div>
    </details>
  );
}

/** D-03: one row per stage - moves into it in the period, Active there now (live), median days of completed stints. */
export function StageMovementCard({ rows }) {
  return (
    <DashCard
      title="Stage movement"
      explain={{
        body: 'Entered counts every move into the stage during the period, from stage history (old stage names count as the new ones). A lead moved into the same stage twice counts twice, so dragging a card in again adds one. Now is the Active leads in that column on the Leads board right now. Median days is for stays in the stage that ended during the period; n is how many.',
        formula:
          'entered = stage history entries into the stage in period (every move counts)\nnow = Active leads in the stage today (live)\nmedian days = median(whole days in stage) over stays that ended in period',
      }}
    >
      <div className="table-responsive">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
          <thead>
            <tr>
              <th style={TH}>Stage</th>
              <th style={THN}>Entered</th>
              <th style={THN}>
                <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                  Now <LivePill />
                </span>
              </th>
              <th style={THN}>Median days in stage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.stage}>
                <td style={TD}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    <StageDot stage={r.stage} />
                    {r.stage}
                  </span>
                </td>
                <td style={{ ...TDN, fontWeight: 700 }}>{r.entered}</td>
                <td style={TDN}>{r.now}</td>
                <td style={TDN}>
                  {r.n > 0 ? (
                    <>
                      {plural(r.medianDays, 'day')} <span style={MUTED}>(n = {r.n})</span>
                    </>
                  ) : (
                    <span style={MUTED}>-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DashCard>
  );
}

function Tile({ label, value, live, color }) {
  return (
    <div style={{ padding: '12px 14px', borderRight: '1px solid #F5F5F2', minWidth: 0 }}>
      <div style={{ ...HEAD, fontSize: 8.5, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        {label}
        {live && <LivePill />}
      </div>
      <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 6, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    </div>
  );
}

function EventRows({ title, events, ownerName }) {
  return (
    <div>
      <div style={{ ...HEAD, fontSize: 8.5, padding: '7px 16px', background: '#FAFAF7', borderBottom: '1px solid #F0F0EC' }}>
        {title} ({events.length})
      </div>
      {events.length === 0 && <div style={{ ...MUTED, padding: '7px 16px', borderBottom: '1px solid #F5F5F2' }}>None in this period.</div>}
      {events.map((e, i) => (
        <button
          key={`${e.id}-${e.ms}-${i}`}
          type="button"
          title={`Open ${e.label}`}
          onClick={() => {
            window.location.hash = leadHash(e.id);
          }}
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            width: '100%',
            padding: '7px 16px',
            background: '#fff',
            border: 'none',
            borderBottom: '1px solid #F5F5F2',
            cursor: 'pointer',
            textAlign: 'left',
            fontFamily: FONT,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 10.5,
              fontWeight: 600,
              color: '#0D0D0D',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {e.label}
          </span>
          <span style={tagSt}>
            {e.from || 'New'} to {e.to}
          </span>
          <span style={tagSt}>{fmtDate(e.day)}</span>
          {e.by && <span style={tagSt}>{ownerName(e.by)}</span>}
        </button>
      ))}
    </div>
  );
}

/** D-04: went Cold and reactivated in the period (status-change events), and Cold right now (live). */
export function ColdSummaryCard({ cold, ownerName }) {
  return (
    <DashCard
      title="Cold summary"
      explain={{
        body: 'Went Cold and Reactivated count status changes made during the period while the record was a Lead. A lead created as Cold counts as going Cold. Reactivated is Cold to Active only. Cold now is the Leads page Cold view right now.',
        formula:
          'went Cold = status changes to Cold (from anything else) in period\nreactivated = status changes Cold to Active in period\ncold now = Cold leads on the board today (live)',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', borderBottom: '1px solid #F0F0EC' }}>
        <Tile label="Went Cold" value={cold.wentCold} color={LEAD_STATUS_COLORS.Cold} />
        <Tile label="Reactivated" value={cold.reactivated} color={LEAD_STATUS_COLORS.Active} />
        <Tile label="Cold now" value={cold.coldNow} live color={LEAD_STATUS_COLORS.Cold} />
      </div>
      <details>
        <summary style={{ padding: '9px 16px', cursor: 'pointer', fontSize: 10.5, fontWeight: 700, color: '#1B5E20' }}>
          Show the status changes
        </summary>
        <div style={{ maxHeight: LIST_MAX, overflowY: 'auto' }}>
          <EventRows title="Went Cold" events={cold.events.wentCold} ownerName={ownerName} />
          <EventRows title="Reactivated" events={cold.events.reactivated} ownerName={ownerName} />
        </div>
      </details>
    </DashCard>
  );
}

/** D-05: the period's new leads by lead source, each counted once at the furthest point it has reached. */
export function SourcePerformanceCard({ perf }) {
  const cols = [
    ['total', 'New'],
    ['dmc', 'Reached Discovery Meeting Complete'],
    ['nda', 'Reached NDA Executed'],
    ['withOpp', 'With an opportunity'],
    ['won', 'Won'],
  ];
  return (
    <DashCard
      title="Lead source performance"
      aside="Leads created in the period"
      style={{ marginBottom: 16 }}
      explain={{
        body: "The leads created in the period (the New leads card), grouped by lead source. Each lead counts once, under its source, at the furthest stage it has reached to date, so moving a card back does not take it out. The columns build on each other: a lead that reached NDA Executed also counts as reaching Discovery Meeting Complete. An opportunity counts when it is linked to the lead's site; Won means one of them is Won or Complete.",
        formula:
          'cohort = New leads in period, by lead source\nreached stage = furthest stage (history or today) >= that stage\nwith an opportunity = at least one linked opportunity\nwon = a linked opportunity with status Won or Complete',
      }}
    >
      <div className="table-responsive">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT }}>
          <thead>
            <tr>
              <th style={TH}>Lead source</th>
              {cols.map(([k, label]) => (
                <th key={k} style={{ ...THN, whiteSpace: 'normal' }}>
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perf.rows.length === 0 && (
              <tr>
                <td colSpan={cols.length + 1} style={{ ...TD, ...MUTED }}>
                  No new leads in this period under these filters.
                </td>
              </tr>
            )}
            {perf.rows.map((r) => (
              <tr key={r.source}>
                <td style={{ ...TD, color: r.total ? '#0D0D0D' : '#9AA29B' }}>{r.source}</td>
                {cols.map(([k]) => (
                  <td key={k} style={{ ...TDN, color: r[k] ? '#0D0D0D' : '#9AA29B' }}>
                    {r[k]}
                  </td>
                ))}
              </tr>
            ))}
            <tr style={{ background: '#F6F9F3' }}>
              <td style={{ ...TD, fontWeight: 800 }}>Total</td>
              {cols.map(([k]) => (
                <td key={k} style={{ ...TDN, fontWeight: 800 }}>
                  {perf.total[k]}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </DashCard>
  );
}

/** D-06: Active leads over 45 days in their current stage, longest first (live). */
export function StalledCard({ rows, undated, ownerName }) {
  return (
    <DashCard
      title="Stalled"
      live
      explain={{
        body: `Active leads that have been in their current stage for more than ${STALL_DAYS} days, the same days the Leads board shows. A lead with no stage date is not listed.`,
        formula: `status = Active and days in current stage > ${STALL_DAYS}`,
      }}
    >
      <div role="region" aria-label="Stalled leads" style={{ maxHeight: LIST_MAX, overflowY: 'auto' }}>
        <NoNextStepGroup
          title={`Over ${STALL_DAYS} days in stage`}
          rows={rows}
          hashOf={leadHash}
          empty={`None - no Active lead has been in its stage over ${STALL_DAYS} days.`}
          detailOf={(r) => (
            <>
              <StageTag stage={r.column} />
              <span style={{ ...tagSt, fontWeight: 700, color: '#B07503' }}>{r.days} days</span>
              {r.owner && <span style={tagSt}>{ownerName(r.owner)}</span>}
            </>
          )}
        />
      </div>
      {undated > 0 && (
        <div style={{ ...MUTED, padding: '8px 16px' }}>
          {plural(undated, 'Active lead has', 'Active leads have')} no stage date yet, so {undated === 1 ? 'it is' : 'they are'} not judged
          here.
        </div>
      )}
    </DashCard>
  );
}

/** D-06: leads with no open task - the same leads as the main Dashboard's No next step list (live). */
export function NoNextStepLeadsCard({ rows, tasks, onRetry, ownerName }) {
  return (
    <DashCard
      title="No next step"
      live
      explain={{
        body: 'Active and Cold leads with no open task, the same leads as the No next step list on the main Dashboard. Every lead must always have a next step.',
        formula: 'lead (Active or Cold) with no open task on it',
      }}
    >
      {tasks.status === 'loading' && <div style={{ ...MUTED, padding: '10px 16px' }}>Loading tasks…</div>}
      {tasks.status === 'error' && (
        <div
          role="alert"
          style={{
            padding: '10px 16px',
            fontSize: 10.5,
            color: '#B71C1C',
            display: 'flex',
            gap: 10,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <span>Tasks could not load, so this list is hidden. {tasks.error}</span>
          <button
            type="button"
            onClick={onRetry}
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: '#1B5E20',
              background: '#fff',
              border: '1px solid #C8E6C9',
              borderRadius: 6,
              padding: '3px 10px',
              cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Try again
          </button>
        </div>
      )}
      {tasks.status === 'ok' && (
        <div role="region" aria-label="Leads with no next step" style={{ maxHeight: LIST_MAX, overflowY: 'auto' }}>
          <NoNextStepGroup
            title="Leads with no open task"
            rows={rows}
            hashOf={leadHash}
            detailOf={(r) => (
              <>
                {r.status === 'Cold' && (
                  <span
                    style={{ fontSize: 8.5, fontWeight: 800, color: '#0277BD', background: '#E1F5FE', borderRadius: 8, padding: '1px 6px' }}
                  >
                    Cold
                  </span>
                )}
                {r.owner && <span style={tagSt}>{ownerName(r.owner)}</span>}
              </>
            )}
          />
        </div>
      )}
    </DashCard>
  );
}
